import React, { useEffect, useRef } from 'react';
import * as echarts from 'echarts';
import { Empty, Spin, Typography } from 'antd';

const { Text } = Typography;

/**
 * 单个飞机的月度BH/FH柱状图
 * data 项支持 { year, month, bh, fh, cycles }；跨年时 X 轴显示 YYYY/M
 */
const MonthlyBhFhBarChart = ({ aircraftReg, data, height = 140, showLabels = true }) => {
    const chartRef = useRef(null);
    const chartInstance = useRef(null);

    useEffect(() => {
        if (!chartRef.current || !data || data.length === 0) return;

        if (!chartInstance.current) {
            chartInstance.current = echarts.init(chartRef.current);
        }

        const years = data.map(d => d.year).filter((y) => y != null);
        const crossYear = years.length > 0 && new Set(years).size > 1;
        const months = data.map(d => {
            if (d.year != null && crossYear) return `${d.year}/${d.month}`;
            if (d.year != null && data.length === 1) return `${d.year}/${d.month}`;
            return `${d.month}月`;
        });
        const bhValues = data.map(d => d.bh);
        const fhValues = data.map(d => d.fh);

        const option = {
            tooltip: {
                trigger: 'axis',
                axisPointer: { type: 'shadow' },
                formatter: (params) => {
                    const index = params[0].dataIndex;
                    const item = data[index];
                    const periodLabel = item.year != null
                        ? `${item.year}年${item.month}月`
                        : `${item.month}月`;
                    return `<strong>${aircraftReg}</strong> - ${periodLabel}<br/>
                            ${params[0].marker}FH: ${params[0].value}h<br/>
                            ${params[1].marker}BH: ${params[1].value}h<br/>
                            航段数: ${item.cycles}`;
                },
                textStyle: { fontSize: 11 }
            },
            grid: {
                left: '3%',
                right: '3%',
                bottom: '15%',
                top: showLabels ? '20%' : '10%',
                containLabel: true
            },
            xAxis: {
                type: 'category',
                data: months,
                axisLabel: {
                    fontSize: 9,
                    color: '#8c8c8c',
                    rotate: crossYear && data.length > 6 ? 30 : 0
                },
                axisLine: { lineStyle: { color: '#d9d9d9' } },
                axisTick: { show: false }
            },
            yAxis: {
                type: 'value',
                axisLabel: { fontSize: 9, color: '#8c8c8c', formatter: '{value}' },
                splitLine: { lineStyle: { color: '#f0f0f0' } },
                axisLine: { show: false }
            },
            series: [
                {
                    name: 'FH',
                    type: 'bar',
                    data: fhValues,
                    itemStyle: { color: '#fa8c16' },
                    label: { show: showLabels, position: 'top', formatter: (p) => p.value != null ? Number(p.value).toFixed(1) : '', fontSize: 10, color: '#fa8c16', fontWeight: 500 },
                    barGap: '15%',
                    barWidth: '30%'
                },
                {
                    name: 'BH',
                    type: 'bar',
                    data: bhValues,
                    itemStyle: { color: '#1890ff' },
                    label: { show: showLabels, position: 'top', formatter: (p) => p.value != null ? Number(p.value).toFixed(1) : '', fontSize: 10, color: '#1890ff', fontWeight: 500 },
                    barWidth: '30%'
                }
            ]
        };

        chartInstance.current.setOption(option, true);

        const observer = new ResizeObserver(() => {
            chartInstance.current?.resize();
        });
        observer.observe(chartRef.current);

        return () => { observer.disconnect(); };
    }, [aircraftReg, data, showLabels]);

    useEffect(() => {
        return () => {
            chartInstance.current?.dispose();
            chartInstance.current = null;
        };
    }, []);

    if (!data || data.length === 0) {
        return (
            <div style={{ height, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#8c8c8c', fontSize: '12px' }}>
                暂无数据
            </div>
        );
    }

    return <div ref={chartRef} style={{ width: '100%', height: `${height}px`, background: 'white', borderRadius: '4px' }} />;
};

/**
 * 月度BH/FH图表行容器（同BhFhChartRow风格）
 */
const MonthlyBhFhChartRow = ({ aircraftReg, data, loading = false }) => {
    return (
        <div style={{
            display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px',
            padding: '8px', background: '#fafafa', borderRadius: '4px',
            border: '1px solid #f0f0f0', transition: 'all 0.3s'
        }}
        onMouseEnter={(e) => { e.currentTarget.style.background = '#f5f5f5'; e.currentTarget.style.borderColor = '#d9d9d9'; }}
        onMouseLeave={(e) => { e.currentTarget.style.background = '#fafafa'; e.currentTarget.style.borderColor = '#f0f0f0'; }}>
            <div style={{
                minWidth: '100px', maxWidth: '100px', fontSize: '13px', fontWeight: 600,
                color: '#1890ff', textAlign: 'center', padding: '8px 12px',
                background: 'white', borderRadius: '4px', border: '1px solid #e6f7ff',
                whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'
            }}>
                {aircraftReg}
            </div>
            <div style={{ flex: 1 }}>
                {loading ? (
                    <div style={{ height: '140px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#8c8c8c', fontSize: '12px' }}>
                        加载中...
                    </div>
                ) : (
                    <MonthlyBhFhBarChart aircraftReg={aircraftReg} data={data} height={140} />
                )}
            </div>
        </div>
    );
};

/**
 * 月度BH/FH柱状图（按飞机分行展示）
 * 数据源：/v1/flight-operation/bh-fh-monthly-series
 */
const MonthlyBhFhChart = ({ data, loading = false }) => {
    if (loading) {
        return <div style={{ textAlign: 'center', padding: 40 }}><Spin /></div>;
    }

    if (!data || data.length === 0) {
        return <Empty description="暂无月度数据" />;
    }

    return (
        <div>
            {data.map(aircraft => (
                <MonthlyBhFhChartRow
                    key={aircraft.aircraft_reg}
                    aircraftReg={aircraft.aircraft_reg}
                    data={aircraft.data}
                />
            ))}
        </div>
    );
};

export default MonthlyBhFhChart;
